import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { ROLE_TO_VARIANT, type PaletteVariant, type StatusRole } from '@/design-system/tokens/status'

/**
 * Badge — статусная метка (`.badge` + `.b-*` макета). Публичный API — пять визуальных вариантов
 * палитры; доменные компоненты задают `role` (семантическую роль), которая маппится на вариант.
 * Цвета — только из словаря светофор-токенов / акцента.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-7 px-2.5 py-0.5 text-12 font-medium',
  {
    variants: {
      variant: {
        red: 'bg-red-bg text-red',
        amber: 'bg-amber-bg text-amber',
        green: 'bg-green-bg text-green',
        gray: 'bg-gray-bg text-gray',
        blue: 'bg-blue-bg text-blue',
      } satisfies Record<PaletteVariant, string>,
    },
    defaultVariants: { variant: 'gray' },
  },
)

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    Omit<VariantProps<typeof badgeVariants>, 'variant'> {
  /** Визуальный вариант палитры (низкоуровневый API примитива). */
  variant?: PaletteVariant
  /** Семантическая роль статуса (для доменных компонентов) — приоритетнее variant. */
  status?: StatusRole
  leftIcon?: ReactNode
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, status, leftIcon, children, ...props }, ref) => {
    const resolved: PaletteVariant = status ? ROLE_TO_VARIANT[status] : (variant ?? 'gray')
    return (
      <span ref={ref} className={cn(badgeVariants({ variant: resolved }), className)} {...props}>
        {leftIcon}
        {children}
      </span>
    )
  },
)
Badge.displayName = 'Badge'

export { badgeVariants }
